import React, { FC } from 'react';
import { compiler } from 'markdown-to-jsx';

import type { DocumentPlace } from '../types';

import { useFetch } from 'app/api';
import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { Modal } from 'app/design-system/components/Modal';
import { Scrollbar } from 'app/design-system/components/Scrollbar';
import { Spinner } from 'app/design-system/components/Spinner';
import { useTranslation } from 'app/lang';
import useComponentWillMount from 'app/lib/hooks/useComponentWillMount';
import LinkWrapper from 'app/widgets/LinkWrapper';

import { getDocumentApiService } from '../utils/get-document-api-service';

import dictionary from '../DocumentModals.dictionary.json';

import styles from '../DocumentModals.module.scss';

// ---

type DocumentModalContentProps = {
  place: DocumentPlace;
  onClose: VoidFunction;
};

const DocumentModalContent: FC<DocumentModalContentProps> = ({
  place,
  onClose,
}) => {
  const { t } = useTranslation();

  const apiService = getDocumentApiService(place);

  const { data, loading, requested, makeRequest } = useFetch(
    apiService,
    { ssrUniqKey: 'footer-documents' },
  );

  useComponentWillMount(() => {
    if (!requested) {
      makeRequest();
    }
  });

  const getModalContent = () => {
    if (loading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    if (!data?.content) {
      return (
        <div className={styles.spinnerContainer}>
          {t(dictionary.contentNotFound)}
        </div>
      );
    }

    return (
      <div className={styles.content}>
        {compiler(data.content, {
          wrapper: null,
          overrides: {
            a: { component: LinkWrapper },
          },
        })}
      </div>
    );
  };

  return (
    <>
      <Modal.Header>
        {data?.name}
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Scrollbar className={styles.modalBodyScroll}>
          {getModalContent()}
        </Scrollbar>
      </Modal.Body>
      <Modal.Footer>
        <Button type={ButtonType.BUTTON}
                intent={ButtonIntent.DEFAULT}
                onClick={onClose}
                className={styles.footerCancelButton}>
          {t(dictionary.modalButtonClose)}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default DocumentModalContent;
